import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { ArrowRight, ArrowLeft, Car, Truck, Ship, Bike, Star, Clock } from "lucide-react";
import { ReviewList } from "@/components/ReviewList";
import { useServices } from "@/hooks/use-services";
import { usePublishedReviews } from "@/hooks/use-reviews";
import { useServiceRatings, getRating } from "@/hooks/use-service-ratings";
import { type Service } from "@/lib/types";
import { ShineDial } from "@/components/ShineDial";
import { useTranslation } from "react-i18next";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

type VehicleFilter = 'all' | 'sedan' | 'suv' | 'cuv' | 'motorcycle' | 'van' | 'caravan' | 'boat';

/** Small four-point sparkle used as an inline separator/accent glyph. */
function SparkleGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="-1.2 -1.2 2.4 2.4" className={cn("w-3 h-3 shrink-0", className)} aria-hidden="true">
      <path d="M0 -1 C0.16 -0.38 0.38 -0.16 1 0 C0.38 0.16 0.16 0.38 0 1 C-0.16 0.38 -0.38 0.16 -1 0 C-0.38 -0.16 -0.16 -0.38 0 -1 Z" fill="currentColor" />
    </svg>
  );
}

/**
 * Refined photo card: the service's real photo with a floating price
 * badge (price for the selected vehicle, or a "from" price on All),
 * then name, description, duration and a book action.
 * Pricing mirrors ServiceCard.getSelectedPrice.
 */
function ServicePhotoCard({
  service,
  vehicleFilter,
  rating,
}: {
  service: Service;
  vehicleFilter: VehicleFilter;
  rating?: { average: number; count: number } | null;
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";

  const name = isRtl ? service.nameAr : service.nameEn;
  const description = isRtl ? service.descriptionAr : service.descriptionEn;
  const shortDescription = description
    .split(/[،,]/)
    .map((item) => item.trim())
    .filter(Boolean)
    .slice(0, 2)
    .join(isRtl ? "، " : ", ");

  const positivePrices = service.vehiclePricing
    ? Object.values(service.vehiclePricing).filter((p): p is number => !!p && p > 0)
    : [];

  let price: string;
  let isFromPrice = false;
  let perFoot = false;
  if (service.isPerUnitPricing) {
    price = Number(service.price).toFixed(3);
    perFoot = true;
  } else if (vehicleFilter !== "all" && service.vehiclePricing?.[vehicleFilter]) {
    price = Number(service.vehiclePricing[vehicleFilter]).toFixed(3);
  } else if (positivePrices.length > 0) {
    price = Math.min(...positivePrices).toFixed(3);
    isFromPrice = positivePrices.length > 1;
  } else {
    price = Number(service.price).toFixed(3);
  }

  const bookHref =
    vehicleFilter !== "all" && service.vehiclePricing?.[vehicleFilter]
      ? `/booking?serviceId=${service.id}&vehicle=${vehicleFilter}`
      : `/booking?serviceId=${service.id}`;

  return (
    // isolate + rounded image wrapper: WebKit doesn't clip transformed
    // children (the hover scale) to an ancestor's border-radius.
    <li className="group relative isolate rounded-3xl border border-border bg-card overflow-hidden hover:border-primary/60 hover:shadow-xl hover:shadow-primary/5 hover:-translate-y-1 transition-all duration-300">
      <Link
        href={bookHref}
        className="flex h-full flex-col"
        data-testid={`menu-service-${service.id}`}
      >
        <div className="relative h-44 overflow-hidden rounded-t-3xl">
          <img
            src={service.imageUrl}
            alt={name}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/5 to-transparent" aria-hidden="true" />
          {service.category && (
            <span className="absolute bottom-3 start-3 z-10 u-eyebrow rounded-full bg-black/45 text-white backdrop-blur-sm px-3 py-1.5">
              {service.category}
            </span>
          )}
          <span className="absolute bottom-3 end-3 z-10 flex items-baseline gap-1 rounded-full bg-card/95 backdrop-blur-sm px-3.5 py-1.5 shadow-md">
            {isFromPrice && (
              <span className="text-[10px] text-muted-foreground">{t("home.menu.from")}</span>
            )}
            <span className="font-display font-bold text-primary">{price}</span>
            <span className="text-[10px] text-muted-foreground">
              {t("services.currency")}
              {perFoot && <>/{t("booking.feet")}</>}
            </span>
          </span>
        </div>

        <div className="flex flex-col flex-1 p-5">
          <div className="flex items-center gap-2.5 flex-wrap mb-1.5">
            <h3 className="font-display font-bold text-lg group-hover:text-primary transition-colors">
              {name}
            </h3>
            {rating && (
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                <Star className="w-3.5 h-3.5 fill-secondary text-secondary" />
                {rating.average.toFixed(1)}
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2 mb-5">{shortDescription}</p>
          <div className="mt-auto flex items-center justify-between gap-3">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border px-2.5 py-1 text-xs text-muted-foreground whitespace-nowrap">
              <Clock className="w-3.5 h-3.5 text-primary" />
              {service.duration} {t("services.minsEstimated")}
            </span>
            <span className="inline-flex items-center rounded-full bg-primary text-primary-foreground text-xs font-semibold px-4 py-2 group-hover:bg-secondary transition-colors">
              {t("services.bookService")}
            </span>
          </div>
        </div>
      </Link>
    </li>
  );
}

export default function Home() {
  const { data: services } = useServices();
  const { data: allReviews } = usePublishedReviews();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [vehicleFilter, setVehicleFilter] = useState<VehicleFilter>('all');

  const avgRating = allReviews && allReviews.length > 0
    ? allReviews.reduce((sum, r) => sum + (r.rating || 0), 0) / allReviews.length
    : 0;
  const reviewCount = allReviews?.length || 0;

  const filteredServices = useMemo(() => {
    if (!services) return [];
    const nonAddOns = services.filter(s => !s.isAddOn);

    if (vehicleFilter === 'all') return nonAddOns;
    // sedan/suv/cuv/motorcycle - show services with pricing > 0 for selected vehicle type
    return nonAddOns.filter(s =>
      s.vehiclePricing &&
      s.vehiclePricing[vehicleFilter] &&
      s.vehiclePricing[vehicleFilter] > 0
    );
  }, [services, vehicleFilter]);

  const filterOptions = useMemo(() => {
    const allOptions: { value: VehicleFilter; label: string; icon?: typeof Car }[] = [
      { value: 'all', label: t("booking.allVehicles") },
      { value: 'sedan', label: t("booking.sedan"), icon: Car },
      { value: 'cuv', label: t("booking.cuv"), icon: Car },
      { value: 'suv', label: t("booking.suv"), icon: Car },
      { value: 'van', label: t("booking.van"), icon: Truck },
      { value: 'motorcycle', label: t("booking.motorcycle"), icon: Bike },
      { value: 'caravan', label: t("booking.caravan"), icon: Truck },
      { value: 'boat', label: t("booking.boat"), icon: Ship },
    ];

    if (!services) return [{ value: 'all' as const, label: t("booking.allVehicles") }];

    const nonAddOns = services.filter(s => !s.isAddOn);

    // Check which vehicle types have services with pricing > 0
    const availableTypes = new Set<VehicleFilter>();

    (['sedan', 'suv', 'cuv', 'motorcycle', 'van', 'caravan', 'boat'] as const).forEach(type => {
      const hasServices = nonAddOns.some(s =>
        s.vehiclePricing &&
        s.vehiclePricing[type] &&
        s.vehiclePricing[type] > 0
      );
      if (hasServices) availableTypes.add(type);
    });

    // Return "All" + only available vehicle types
    return allOptions.filter(option =>
      option.value === 'all' || availableTypes.has(option.value)
    );
  }, [services, t]);

  const marqueeItems = t("home.marquee", { returnObjects: true }) as string[];
  const howSteps = t("home.how.steps", { returnObjects: true }) as { t: string; d: string }[];
  const { data: serviceRatings } = useServiceRatings();

  return (
    <div className="min-h-screen overflow-x-hidden">
      {/* ── Hero: the slogan + the Shine Dial ─────────────────────── */}
      <section className="relative bg-background py-20 lg:py-28 overflow-x-hidden">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-14 lg:gap-6 items-center">
            <div className="max-w-xl animate-fade-in-up">
              <p className="u-eyebrow text-secondary mb-6">
                {t("hero.eyebrow")}
              </p>
              <h1 className="font-display font-bold leading-[1.04] text-4xl md:text-5xl lg:text-6xl mb-6">
                {t("hero.slogan1")}
                <br />
                <span className="text-primary">{t("hero.slogan2")}</span>
              </h1>
              <p className="text-muted-foreground mb-9 text-lg leading-relaxed max-w-md">
                {t("hero.description")}
              </p>
              <div className="flex flex-wrap items-center gap-4">
                <Link href="/booking">
                  <Button size="lg" className="h-12 rounded-full px-9 text-base">
                    {t("hero.bookNow")}
                  </Button>
                </Link>
                <Link href="/services">
                  <Button variant="outline" size="lg" className="h-12 rounded-full px-8 text-base">
                    {t("hero.ourServices")}
                  </Button>
                </Link>
              </div>
              {avgRating > 0 && (
                <div
                  className="inline-flex items-center gap-2.5 rounded-full border border-border bg-card px-4 py-2 mt-9 shadow-sm"
                  data-testid="hero-rating"
                >
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star
                        key={s}
                        className={cn(
                          "h-4 w-4",
                          s <= Math.round(avgRating)
                            ? "fill-secondary text-secondary"
                            : "text-muted-foreground/30",
                        )}
                      />
                    ))}
                  </div>
                  <span className="text-base font-semibold">{avgRating.toFixed(1)}</span>
                  <span className="text-sm text-muted-foreground">
                    ({reviewCount} {reviewCount === 1 ? t("reviews.single") : t("reviews.plural")})
                  </span>
                </div>
              )}
            </div>

            <div className="relative animate-fade-in-right">
              <div className="absolute inset-0 -z-10 bg-[radial-gradient(closest-side,hsl(var(--accent)),transparent)] opacity-80 dark:opacity-20 blur-2xl" aria-hidden="true" />
              <ShineDial />
            </div>
          </div>
        </div>
      </section>

      {/* ── Ticker: the brand promise on repeat ───────────────────── */}
      <section className="bg-primary text-primary-foreground py-3.5 overflow-hidden" aria-hidden="true">
        <div className="tc-ticker-track">
          {Array.from({ length: 12 }, (_, copy) => copy).map((copy) => (
            <div key={copy} className="flex items-center shrink-0">
              {marqueeItems.map((item, i) => (
                <span key={`${copy}-${i}`} className="u-eyebrow inline-flex items-center gap-6 px-6 whitespace-nowrap">
                  {item}
                  <SparkleGlyph className="opacity-70" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* ── How it works: a real 3-step schedule ──────────────────── */}
      <section className="py-24 bg-card">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mb-14">
            <p className="u-eyebrow text-secondary mb-3">{t("home.how.eyebrow")}</p>
            <h2 className="text-3xl md:text-4xl font-bold font-display">{t("home.how.title")}</h2>
          </div>

          <div className="relative max-w-2xl">
            {/* dashed line linking the schedule, aligned to marker centers */}
            <div className="absolute top-6 bottom-6 start-6 border-s-2 border-dashed border-primary/30" aria-hidden="true" />
            <ol className="space-y-12">
              {howSteps.map((step, i) => (
                <li key={i} className="relative flex gap-6 items-start">
                  <span className="relative z-10 w-12 h-12 shrink-0 rounded-full border-2 border-primary bg-card text-primary font-mono font-bold text-sm flex items-center justify-center">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="pt-2.5">
                    <h3 className="text-xl font-bold mb-1.5">{step.t}</h3>
                    <p className="text-muted-foreground leading-relaxed">{step.d}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ── Services menu (live data + vehicle filter) ────────────── */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6">
            <div className="max-w-xl">
              <p className="u-eyebrow text-secondary mb-3">{t("services.eyebrow")}</p>
              <h2 className="text-3xl md:text-4xl font-bold mb-4 font-display">{t("services.title")}</h2>
              <p className="text-muted-foreground">{t("services.subtitle")}</p>
            </div>
            <Link href="/services">
              <Button variant="outline" className="group rounded-full">
                {t("services.viewAll")}
                {isRTL ? (
                  <ArrowLeft className="me-2 w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                ) : (
                  <ArrowRight className="ms-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                )}
              </Button>
            </Link>
          </div>

          {/* Vehicle Type Filter Pills */}
          <div className="flex flex-wrap gap-2 mb-2 overflow-x-hidden">
            {filterOptions.map((option) => (
              <Badge
                key={option.value}
                variant={vehicleFilter === option.value ? "default" : "outline"}
                className={cn(
                  "cursor-pointer rounded-full px-4 py-2 text-sm transition-all whitespace-nowrap",
                  vehicleFilter === option.value && "bg-primary text-primary-foreground"
                )}
                onClick={() => setVehicleFilter(option.value)}
                data-testid={`home-filter-${option.value}`}
              >
                {option.icon && <option.icon className="w-4 h-4 me-1.5" />}
                {option.label}
              </Badge>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mb-6">{t("home.menu.note")}</p>

          {/* The wash menu */}
          <ul className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredServices.slice(0, 6).map((service) => (
              <ServicePhotoCard
                key={service.id}
                service={service}
                vehicleFilter={vehicleFilter}
                rating={getRating(serviceRatings, service.id)}
              />
            ))}
          </ul>
        </div>
      </section>

      {/* ── Why Time Clean ────────────────────────────────────────── */}
      <section className="py-24 bg-card relative">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-[2fr,3fr] gap-12 lg:gap-20 items-start">
            <div className="lg:sticky lg:top-28">
              <p className="u-eyebrow text-secondary mb-3">{t("features.eyebrow")}</p>
              <h2 className="text-3xl md:text-4xl font-bold mb-4 font-display">{t("features.title")}</h2>
              <p className="text-muted-foreground leading-relaxed">{t("features.subtitle")}</p>
            </div>

            <ul className="divide-y divide-border/60">
              {[
                {
                  title: t("features.royalService"),
                  desc: t("features.royalServiceDesc")
                },
                {
                  title: t("features.paintProtection"),
                  desc: t("features.paintProtectionDesc")
                },
                {
                  title: t("features.expressService"),
                  desc: t("features.expressServiceDesc")
                }
              ].map((feature, i) => (
                <li key={i} className="group flex gap-5 py-8 first:pt-0 last:pb-0">
                  <SparkleGlyph className="w-6 h-6 mt-1.5 text-primary transition-transform duration-300 group-hover:rotate-45" />
                  <div>
                    <h3 className="text-2xl font-bold font-display mb-2 transition-colors group-hover:text-primary">
                      {feature.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">{feature.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ── Reviews ───────────────────────────────────────────────── */}
      <section className="py-24 bg-background border-y border-border/50">
        <div className="container mx-auto px-4 text-center">
          <p className="u-eyebrow text-secondary mb-3">{t("reviews.eyebrow")}</p>
          <h2 className="text-3xl md:text-4xl font-bold mb-16 font-display">{t("reviews.title")}</h2>
          <ReviewList />
        </div>
      </section>

      {/* ── FAQ ───────────────────────────────────────────────────── */}
      <section className="py-24 bg-muted/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <p className="u-eyebrow text-secondary mb-3">{t("faq.eyebrow")}</p>
            <h2 className="text-3xl md:text-4xl font-bold font-display">{t("faq.title")}</h2>
          </div>
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="w-full">
              {(t("faq.items", { returnObjects: true }) as { question: string; answer: string }[]).map((item, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border-b border-border">
                  <AccordionTrigger className="text-start py-4 hover:no-underline" data-testid={`faq-question-${index}`}>
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-4">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* ── Closing CTA: navy band with the arc + sparkle ─────────── */}
      <section className="relative hero-navy text-white py-24 overflow-hidden">
        <svg
          viewBox="0 0 480 480"
          className="absolute -top-32 -end-24 w-[420px] h-[420px] opacity-[0.12] pointer-events-none"
          aria-hidden="true"
        >
          <circle cx="240" cy="240" r="180" fill="none" stroke="currentColor" strokeWidth="10" strokeLinecap="round" strokeDasharray="800 331" />
          <path d="M0 -1 C0.16 -0.38 0.38 -0.16 1 0 C0.38 0.16 0.16 0.38 0 1 C-0.16 0.38 -0.38 0.16 -1 0 C-0.38 -0.16 -0.16 -0.38 0 -1 Z" transform="translate(420 240) scale(26)" fill="currentColor" />
        </svg>
        <div className="container mx-auto px-4 relative">
          <div className="max-w-xl">
            <p className="u-eyebrow text-secondary mb-3">{t("home.cta.eyebrow")}</p>
            <h2 className="text-3xl md:text-5xl font-bold font-display mb-4 text-white">{t("home.cta.title")}</h2>
            <p className="text-white/70 text-lg mb-9">{t("home.cta.body")}</p>
            <Link href="/booking">
              <Button size="lg" className="h-12 rounded-full px-9 text-base">
                {t("home.cta.button")}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
